import {uuid4} from '@sentry/utils'
import {UIEventEmitter} from '../../../../shared/scripts/UIEventEmitter'
import {UIDIFrameCommunicationCommandType, UIDCustomEvent, UIDIFrameCommunicationMessageData, UIDServerProfile, UIDUserPermissions, DiscordEvent} from '../../../../../shared/DiscordActivityTypes'
import {UIDParentIFramePopupHandler} from './UIDParentIFramePopupHandler'

interface PromiseCallbacks {
    resolve: (value: unknown) => unknown
    reject: (reason: unknown) => unknown
}

export class UIDChildIFrameCommunicationHandler {
    voiceChannelId: string
    inDiscordServer: boolean
    userDiscordAccessToken: string
    serverProfile: UIDServerProfile
    userPermissions: UIDUserPermissions
    referrerId: string | undefined
    referralTypeId: string | undefined
    isInitialLoad: boolean

    private readonly readyPromise: Promise<void>
    private readonly messagesWaitingForParentResponse: Map<string, PromiseCallbacks> = new Map()

    readonly parentSubscriptionListener = new UIEventEmitter()
    private readonly parentPopupHandler: UIDParentIFramePopupHandler

    constructor() {
        window.addEventListener('message', (data) => this.handleMessageReceivedFromParent(data))
        this.parentPopupHandler = new UIDParentIFramePopupHandler(this)

        this.readyPromise = new Promise<void>((resolve) => {
            this.parentSubscriptionListener.subscribeOnce(UIDCustomEvent.Ready, ({instanceId, serverId, userDiscordAccessToken, serverProfile, userPermissions, referrerId, referralTypeId, isInitialLoad}) => {
                this.voiceChannelId = instanceId
                this.inDiscordServer = serverId != null
                this.userDiscordAccessToken = userDiscordAccessToken
                this.serverProfile = serverProfile
                this.userPermissions = userPermissions
                this.referrerId = referrerId
                this.referralTypeId = referralTypeId
                this.isInitialLoad = isInitialLoad
                resolve()
            })
        })

        void this.sendMessageToParent({command: UIDIFrameCommunicationCommandType.ChildReady})
    }

    async waitToBeReady() {
        return this.readyPromise
    }

    async subscribeToDiscordEvent(event: DiscordEvent, callback: (data: any) => any) {
        const listenerCount = this.parentSubscriptionListener.listenerCount(event)

        const firstTimeSubscribing = listenerCount == 0
        if(firstTimeSubscribing) {
            await this.sendMessageToParent({
                command: UIDIFrameCommunicationCommandType.Subscribe,
                subscription: event,
            })
        }

        const listener = this.parentSubscriptionListener.subscribe(event, callback)
        return listener
    }

    async unsubscribeFromDiscordEvent(event: DiscordEvent, callback: (data: any) => any) {
        const listenerCount = this.parentSubscriptionListener.listenerCount(event)

        const onlyOneListenerLeft = listenerCount == 1
        if(onlyOneListenerLeft) {
            await this.sendMessageToParent({
                command: UIDIFrameCommunicationCommandType.Unsubscribe,
                event: event,
            })
        }

        this.parentSubscriptionListener.unsubscribe(event, callback)
    }

    handleMessageReceivedFromParent({data: messageData, origin}: MessageEvent<UIDIFrameCommunicationMessageData>) {
        if(typeof messageData !== 'object' || Array.isArray(messageData) || messageData === null) return

        try {
            const referrerOrigin = new URL(document.referrer).origin
            if(origin !== referrerOrigin) return
        } catch(e) {
            return
        }

        const {messageId, event, command, data, args} = messageData

        if(event === UIDCustomEvent.ParentIFramePopup) {
            this.parentPopupHandler.createPopup(data)
            return
        }

        if(command === UIDIFrameCommunicationCommandType.DispatchDiscordEvent) {
            if(event == null) throw new Error('Event is missing')
            this.parentSubscriptionListener.emit(event, data)
        }

        if(messageId == undefined) return

        // Resolve the promise that was waiting for a response from the parent
        this.messagesWaitingForParentResponse.get(messageId)?.resolve(data)
        this.messagesWaitingForParentResponse.delete(messageId)
    }

    sendMessageToParent(messageData: UIDIFrameCommunicationMessageData): Promise<any> {
        // eslint-disable-next-line no-console
        console.log('Sending message', messageData)
        const messageId = uuid4()
        window.parent.postMessage({
            messageId: messageId,
            ...messageData,
        }, document.referrer ? document.referrer : '*')

        const waitUntilParentRespondsPromise = new Promise((resolve, reject) => {
            this.messagesWaitingForParentResponse.set(messageId, {resolve: resolve, reject: reject})
        })

        return waitUntilParentRespondsPromise
    }
}
